import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import { personaOfMessage } from '@crowdex/agent-contracts/api';
import type { ReactNode } from 'react';
import { PersonaMark } from '@/features/assistants/persona-mark';

/** A colour of its own for the persona, the same in every conversation (by its id). */
function personaIndex(id: string): number {
  let hash = 0;
  for (const char of id) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return hash;
}

/**
 * A persona's one reply in the thread (design D51, mobile 49): "Designer joined for this reply",
 * then the reply under its avatar and name ("Designer · Claude Code · replies once"), and a note
 * that the conversation stays with its agent.
 */
export function PersonaReply({
  message,
  children,
}: {
  message: CrowdexUIMessage;
  children: ReactNode;
}) {
  const persona = personaOfMessage(message);
  if (persona === null) return <>{children}</>;
  const index = personaIndex(persona.id);
  return (
    <section
      aria-label={`${persona.name} replies once`}
      className="flex w-full flex-col gap-3"
      data-persona-reply={persona.id}
    >
      <div className="type-meta flex items-center gap-3 text-muted-foreground">
        <span aria-hidden className="h-px flex-1 bg-hairline" />
        <span className="flex items-center gap-1.5">
          <PersonaMark index={index} persona={persona} size="xs" />
          {persona.name} joined for this reply
        </span>
        <span aria-hidden className="h-px flex-1 bg-hairline" />
      </div>
      <div className="flex gap-3">
        <PersonaMark index={index} persona={persona} size="md" />
        <div className="min-w-0 flex-1">
          <p className="type-meta">
            <span className="font-semibold text-foreground">{persona.name}</span>
            <span className="text-muted-foreground">
              {persona.agentName === null ? '' : ` · ${persona.agentName}`} · replies once
              {persona.readOnly === true ? ' · read-only, on another agent' : ''}
            </span>
          </p>
          <div className="mt-1 flex flex-col gap-2">{children}</div>
        </div>
      </div>
      <p className="type-meta text-muted-foreground">
        The conversation stays with its agent. @ again to bring {persona.name} back.
      </p>
    </section>
  );
}
